import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import '@/App.css';
import { FileText, GlassWater, Menu, Store, Truck, UtensilsCrossed, X } from 'lucide-react';

// Contexts
import { CartProvider } from '@/context/CartContext';
import { AuthProvider } from '@/context/AuthContext';
import { PushNotificationProvider } from '@/context/PushNotificationContext';

// Public pages components
import { HeroEcommerce } from '@/components/HeroEcommerce';
import { DailyMenuSection } from '@/components/DailyMenuSection';
import { FullMenuSection } from '@/components/FullMenuSection';
import { FulfillmentSelector } from '@/components/FulfillmentSelector';
import { CheckoutSection } from '@/components/CheckoutSection';
import { CartSheet } from '@/components/CartSheet';
import { CartButton } from '@/components/CartButton';
import { Footer } from '@/components/Footer';
import { AboutPage } from '@/components/AboutPage';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';

// Admin components
import { AdminLogin } from '@/pages/admin/AdminLogin';
import { AdminDashboard } from '@/pages/admin/AdminDashboard';
import { AdminAccount } from '@/pages/admin/AdminAccount';
import AdminMenus from '@/pages/admin/AdminMenus';
import AdminNotifications from '@/pages/admin/AdminNotifications';
import AdminFullMenu from '@/pages/admin/AdminFullMenu';
import AdminAboutUs from '@/pages/admin/AdminAboutUs';
import AdminOrdersTrash from '@/pages/admin/AdminOrdersTrash';

import { ProtectedRoute } from '@/components/ProtectedRoute';
import { AdminLayout } from '@/components/AdminLayout';
import { PushNotificationStack } from '@/components/PushNotificationStack';

import { Toaster } from '@/components/ui/sonner';

// Home page
const MenuPage = () => {
  return (
    <CartProvider>
      <div className="min-h-screen bg-background">
        <HeaderWithCart />

        <main>
          <HeroEcommerce />
          <DailyMenuSection />
          <FulfillmentSelector />
          <CheckoutSection />
          <Footer />
        </main>

        <CartSheet />
      </div>
    </CartProvider>
  );
};

// Full menu page
const FullMenuPage = () => {
  return (
    <CartProvider>
      <div className="min-h-screen bg-background">
        <HeaderWithCart />

        <main>
          <FullMenuSection />
          <Footer />
        </main>

        <CartSheet />
      </div>
    </CartProvider>
  );
};

const AboutPublicPage = () => {
  return (
    <CartProvider>
      <div className="min-h-screen bg-background">
        <HeaderWithCart />

        <main>
          <AboutPage />
          <Footer />
        </main>

        <CartSheet />
      </div>
    </CartProvider>
  );
};

// Header component with cart button
const HeaderWithCart = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navItems = [
    {
      label: 'Consegna a domicilio',
      icon: Truck,
      action: () => {
        if (location.pathname !== '/') {
          navigate('/#modalita-ordine');
          return;
        }

        const el = document.getElementById('modalita-ordine');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      },
    },
    {
      label: 'Ritira in sede',
      icon: Store,
      action: () => {
        if (location.pathname !== '/') {
          navigate('/#modalita-ordine');
          return;
        }

        const el = document.getElementById('modalita-ordine');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      },
    },
    {
      label: 'Bevande',
      icon: GlassWater,
      action: () => navigate('/menu-completo#bevande'),
    },
    {
      label: 'Menu completo',
      icon: UtensilsCrossed,
      action: () => {
        navigate('/menu-completo');
        window.setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 120);
      },
    },
    {
      label: 'Chi siamo',
      icon: FileText,
      action: () => {
        navigate('/chi-siamo');
        window.setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 120);
      },
    },
  ];

  useEffect(() => {
    if (!location.hash) return;

    const targetId = location.hash.replace('#', '');
    const timeoutId = window.setTimeout(() => {
      const target = document.getElementById(targetId);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 160);

    return () => window.clearTimeout(timeoutId);
  }, [location.pathname, location.hash]);

  const handleNavClick = (action) => {
    setIsMobileMenuOpen(false);
    action();
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-md shadow-medium">
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_sapori-menu/artifacts/mgkf5kqb_sapori%20logo.jpg"
              alt="Sapori 1989"
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover shadow-soft"
            />
            <div className="hidden sm:block">
              <h1 className="text-lg font-bold text-foreground leading-tight">
                Sapori 1989
              </h1>
              <p className="text-xs text-muted-foreground">
                Menu Digitale & Ordini
              </p>
            </div>
          </div>

          <nav className="hidden xl:flex items-center gap-2">
            {navItems.map((item) => {
              const Icon = item.icon;

              return (
                <button
                  key={item.label}
                  type="button"
                  onClick={item.action}
                  className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium text-foreground/78 transition-colors hover:bg-muted hover:text-foreground"
                >
                  <Icon className="h-4 w-4 text-[#8f7f74]" />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            <CartButton />

            <div className="xl:hidden">
              <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-11 w-11 rounded-2xl border-[#d8ccc3] bg-white/90 shadow-sm"
                  >
                    {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                  </Button>
                </SheetTrigger>

                <SheetContent side="right" className="w-[300px] border-l border-[#e5d9d0] bg-[#fffaf6] p-0">
                  <SheetTitle className="sr-only">Menu di navigazione</SheetTitle>

                  <div className="flex h-full flex-col">
                    <div className="border-b border-[#eaded5] px-5 py-5">
                      <div className="flex items-center gap-3">
                        <img
                          src="https://customer-assets.emergentagent.com/job_sapori-menu/artifacts/mgkf5kqb_sapori%20logo.jpg"
                          alt="Sapori 1989"
                          className="h-12 w-12 rounded-full object-cover shadow-soft"
                        />
                        <div>
                          <h2 className="text-base font-bold text-foreground">Sapori 1989</h2>
                          <p className="text-xs text-muted-foreground">Navigazione rapida</p>
                        </div>
                      </div>
                    </div>

                    <nav className="flex-1 px-4 py-5">
                      <div className="space-y-2">
                        {navItems.map((item) => {
                          const Icon = item.icon;

                          return (
                            <button
                              key={item.label}
                              type="button"
                              onClick={() => handleNavClick(item.action)}
                              className="flex w-full items-center gap-3 rounded-2xl border border-[#eaded5] bg-white px-4 py-3 text-left text-sm font-medium text-foreground shadow-sm transition-colors hover:bg-[#f8efe8]"
                            >
                              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#f3e7dd] text-[#8f7f74]">
                                <Icon className="h-5 w-5" />
                              </span>
                              <span>{item.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </nav>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

// Admin pages with Layout
const AdminDashboardPage = () => (
  <AdminLayout>
    <AdminDashboard />
  </AdminLayout>
);

const AdminAccountPage = () => (
  <AdminLayout>
    <AdminAccount />
  </AdminLayout>
);

const AdminFullMenuPage = () => (
  <AdminLayout>
    <AdminFullMenu />
  </AdminLayout>
);

const AdminAboutUsPage = () => (
  <AdminLayout>
    <AdminAboutUs />
  </AdminLayout>
);

const AdminOrdersTrashPage = () => (
  <AdminLayout>
    <AdminOrdersTrash />
  </AdminLayout>
);

function App() {
  return (
    <BrowserRouter basename="/menu-digitale">
      <AuthProvider>
        <PushNotificationProvider>
          <Routes>
              <Route path="/" element={<MenuPage />} />
              <Route path="/menu-completo" element={<FullMenuPage />} />
              <Route path="/chi-siamo" element={<AboutPublicPage />} />

            <Route path="/admin/login" element={<AdminLogin />} />

            <Route element={<ProtectedRoute />}>
              <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
              <Route path="/admin/notifications" element={<AdminNotifications />} />
              <Route path="/admin/menus" element={<AdminMenus />} />
              <Route path="/admin/fixed-menu" element={<Navigate to="/admin/full-menu" replace />} />
              <Route path="/admin/items" element={<Navigate to="/admin/full-menu" replace />} />
              <Route path="/admin/full-menu" element={<AdminFullMenuPage />} />
              <Route path="/admin/about-us" element={<AdminAboutUsPage />} />
              <Route path="/admin/orders-trash" element={<AdminOrdersTrashPage />} />
              <Route path="/admin/account" element={<AdminAccountPage />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>

          <PushNotificationStack />
          <Toaster position="top-center" />
        </PushNotificationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
